import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MailCheck, Send } from "lucide-react";

export default function DigestTab() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState("daily");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/admin/companies/${companyId}/digest`).then((r) => {
      setEnabled(!!r.data.enabled);
      setFrequency(r.data.frequency || "daily");
    }).catch(() => {});
  }, [companyId]);

  const save = async () => {
    try {
      await api.put(`/admin/companies/${companyId}/digest`, { enabled, frequency });
      toast.success("Digest settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/companies/${companyId}/digest/test`);
      toast.success("Test digest sent to your inbox");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="digest-settings-card">
        <div className="flex items-center gap-2 mb-1">
          <MailCheck className="h-4 w-4 text-slate-400" />
          <div className="font-heading text-lg font-semibold">Fatigue digest emails</div>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Automatically email a per-unit fatigue summary to company and unit admins.
          Daily digests cover yesterday and send at 06:00 UTC; weekly digests send on Mondays and cover the last 7 days.
          Each digest includes &quot;Most at-risk&quot; and &quot;Most improved&quot; member highlights.
        </p>
        {isSuper && companies.length > 1 && (
          <div className="mb-4 max-w-sm">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="digest-company-select"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm mb-4">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
            data-testid="digest-enabled-toggle" className="accent-blue-600 h-4 w-4" />
          Enable digest emails for this company
        </label>
        <div className="max-w-xs mb-4">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Frequency</label>
          <select value={frequency} onChange={(e) => setFrequency(e.target.value)} data-testid="digest-frequency-select"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="daily">Daily (yesterday&apos;s summary)</option>
            <option value="weekly">Weekly (Mondays, last 7 days)</option>
          </select>
        </div>
        <div className="flex gap-3">
          <button onClick={save} data-testid="digest-save-btn"
            className="px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save settings</button>
          <button onClick={sendTest} disabled={busy} data-testid="digest-test-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-sm disabled:opacity-60">
            <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send test digest to me"}
          </button>
        </div>
      </div>
    </div>
  );
}
